import {useMap} from '@vis.gl/react-google-maps'
import {useCallback, useEffect, useRef} from 'react'

import {
    GOOGLE_MAPS_CANCELLED,
    GOOGLE_MAPS_READY,
    type GoogleMapsOperation,
    type GoogleMapsOperationResult,
    type GoogleMapsSource,
    recordDynamicMapInstance,
    startGoogleMapsOperation,
} from './google-maps-metrics'
import {type GoogleMapsProviderStatus, useGoogleMapsProviderStatus} from './google-maps-provider'

export type MapLoadTracking = {
    /** Call from the map's first `onTilesLoaded`; repeated calls are ignored. */
    markReady: () => void
    /** Call when the map's error boundary catches a runtime failure. */
    markRuntimeError: () => void
    observeMap: (map: object) => void
}

/** Maps a known provider failure to the result it gives pending attempts, or null if healthy. */
function providerFailureResult(
    status: GoogleMapsProviderStatus,
): Extract<GoogleMapsOperationResult, {outcome: 'error'}> | null {
    switch (status) {
        case 'missing':
            return {outcome: 'error', reason: 'missing_key'}
        case 'script-load-failed':
            return {outcome: 'error', reason: 'script_load'}
        case 'authentication-failed':
            return {outcome: 'error', reason: 'authentication'}
        default:
            return null
    }
}

/**
 * Tracks one map display request for the lifetime of the calling component.
 * Call it above any missing-key or provider-failure guard that suppresses the
 * map so those requests still count as failed attempts.
 */
export function useMapLoadTracking(source: GoogleMapsSource): MapLoadTracking {
    const providerStatus = useGoogleMapsProviderStatus()
    const sourceRef = useRef(source)
    const providerStatusRef = useRef(providerStatus)
    const operationRef = useRef<GoogleMapsOperation | null>(null)
    const observedMapRef = useRef<object | null>(null)
    // A render failure is caught before the effect below starts the attempt.
    const earlyResultRef = useRef<GoogleMapsOperationResult | null>(null)

    sourceRef.current = source
    providerStatusRef.current = providerStatus

    const beginOperation = useCallback(() => {
        const operation = startGoogleMapsOperation(sourceRef.current, 'dynamic_map')
        if (observedMapRef.current) {
            operation.recordInstance(
                recordDynamicMapInstance(observedMapRef.current, sourceRef.current),
            )
        }
        const knownResult =
            earlyResultRef.current ?? providerFailureResult(providerStatusRef.current)
        earlyResultRef.current = null
        if (knownResult) {
            operation.finish(knownResult)
        }
        operationRef.current = operation
    }, [])

    useEffect(() => {
        beginOperation()
        return () => {
            operationRef.current?.finish(GOOGLE_MAPS_CANCELLED)
            operationRef.current = null
            observedMapRef.current = null
        }
    }, [beginOperation])

    useEffect(() => {
        const failure = providerFailureResult(providerStatus)
        if (failure) {
            operationRef.current?.finish(failure)
        }
    }, [providerStatus])

    const markReady = useCallback(() => operationRef.current?.finish(GOOGLE_MAPS_READY), [])
    const markRuntimeError = useCallback(() => {
        const result: GoogleMapsOperationResult = {outcome: 'error', reason: 'runtime'}
        if (operationRef.current) {
            operationRef.current.finish(result)
        } else {
            earlyResultRef.current = result
        }
    }, [])
    const observeMap = useCallback(
        (map: object) => {
            if (observedMapRef.current === map) {
                return
            }
            const isReplacement = observedMapRef.current !== null
            observedMapRef.current = map
            if (isReplacement) {
                operationRef.current?.finish(GOOGLE_MAPS_CANCELLED)
                beginOperation()
            } else if (operationRef.current) {
                operationRef.current.recordInstance(
                    recordDynamicMapInstance(map, sourceRef.current),
                )
            }
        },
        [beginOperation],
    )

    return {markReady, markRuntimeError, observeMap}
}

/** Renders inside `<Map>` to report the actual map object through the map context. */
export function MapLoadObserver({tracking}: {tracking: MapLoadTracking}) {
    const map = useMap()
    const {observeMap} = tracking

    useEffect(() => {
        if (map) {
            observeMap(map)
        }
    }, [map, observeMap])

    return null
}
